import {AxoisRequestManager} from "../../stores/managers/RequestManager";
import {from} from "rxjs";
import type SitApi from "../SitApit";
import type {Category, Company, Target} from "../ApiTypes";
import type {PageResponse} from "~/services/ApiTypes.ts";
import {makeAutoObservable} from "mobx";
import {inOrNotIn} from "./sitFilterParams";
import type AuthStore from "../../authentication/AuthStore";

export type TargetFilterValues = Pick<TargetListStore, 'my_targets' | 'categories' | 'companies' | 'excluded'>;

export class TargetListStore {
    // filters
    search: string = '';
    my_targets: number = 0;
    categories: Category[] = [];
    companies: Company[] = [];
    /** Filters in "Excluding" mode: 'companies', 'categories'. */
    excluded: string[] = [];
    per_page = 25;
    page = 0;
    ordering = '-saving_amount';
    // Debounce handle for the search box.
    private searchTimer?: ReturnType<typeof setTimeout>;

    readonly targetListRM = new AxoisRequestManager<{filters: string[][]}, PageResponse<Target>>(
        ({filters}) => from(this.api.listTargets(filters)));

    constructor(private api: SitApi, private authStore: AuthStore) {
        makeAutoObservable(this, {});
    }

    /**
     * Rebuild the filter list from the current field state and reload. Any filter
     * change resets to page 0 (`resetPage`); only pagination keeps the page.
     */
    reloadData(onlyIfEmpty = false, resetPage = true) {
        if (onlyIfEmpty && (this.targetListRM.result !== undefined || this.targetListRM.busy))
            return;
        if (resetPage)
            this.page = 0;
        const filters: [string, string][] = [];
        if (this.search.trim().length > 0) filters.push(['search', this.search.trim()]);
        if (this.my_targets && this.authStore.sitUserProfile?.id) filters.push(['owner', String(this.authStore.sitUserProfile.id)]);
        if (this.companies.length > 0) filters.push([inOrNotIn('companies__id', this.excluded.includes('companies')), this.companies.map(c => c.id).toString()]);
        if (this.categories.length > 0) filters.push([inOrNotIn('categories__id', this.excluded.includes('categories')), this.categories.map(c => c.id).toString()]);
        filters.push(['page_size', String(this.per_page)]);
        if (this.page > 0) filters.push(['page', String(this.page + 1)]);
        if (this.ordering !== '') filters.push(['ordering', this.ordering]);
        this.targetListRM.request({filters});
    }

    /** Clear all filters back to defaults (e.g. after adding/updating a target). */
    reset() {
        this.search = '';
        this.my_targets = 0;
        this.categories = [];
        this.companies = [];
        this.page = 0;
        this.ordering = '-saving_amount';
    }

    setSearch(value: string) {
        // Update the field immediately (responsive input), debounce the reload.
        this.search = value;
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.reloadData(), 350);
    }

    /** Set several filters at once (the filter bar's Apply) and reload once. */
    applyFilters(f: TargetFilterValues) {
        this.my_targets = f.my_targets;
        this.categories = f.categories;
        this.companies = f.companies;
        this.excluded = f.excluded;
        this.reloadData();
    }

    setMyTargets(value: number) {
        this.my_targets = value;
        this.reloadData();
    }

    setCategories(value: Category[]) {
        this.categories = value;
        this.reloadData();
    }

    setCompanies(value: Company[]) {
        this.companies = value;
        this.reloadData();
    }

    setPerPage(value: number) {
        if (value > 0) {
            this.per_page = value;
            this.reloadData();
        }
    }

    setPage(value: number) {
        if (value >= 0) {
            this.page = value;
            this.reloadData(false, false);
        }
    }

    setOrdering(value: string) {
        this.ordering = value;
        this.reloadData();
    }
}
